<template>
  <HrPageLayout
    title="Retirement Management"
    subtitle="Mandatory retirement age, milestone windows, and workforce transition — aligned to county policy."
    :breadcrumb-items="[{ label: 'Public Service', route: '/dashboard' }, { label: 'Retirement Management' }]"
  >
    <div class="rm-module">
      <nav class="rm-tabs" aria-label="Retirement sections">
        <button
          v-for="t in tabs"
          :key="t.id"
          type="button"
          class="rm-tab"
          :class="{ 'rm-tab--active': activeTab === t.id }"
          @click="activeTab = t.id; load()"
        >
          <i class="bx rm-tab__icon" :class="t.icon" aria-hidden="true"></i>
          {{ t.label }}
        </button>
      </nav>

      <!-- Policy -->
      <section v-show="activeTab === 'policy'" class="rm-panel">
        <header class="rm-panel__head">
          <div>
            <h2 class="rm-panel__title">Retirement policy settings</h2>
            <p class="rm-panel__desc">
              These values drive eligibility lists, notification due dates, and emails county-wide. Defaults match the former 60-year / 2-year / 1-year model; adjust to match gazetted rules.
            </p>
          </div>
        </header>

        <div class="rm-policy-grid">
          <div class="rm-field rm-field--highlight">
            <label for="ret-age">Mandatory retirement age (years)</label>
            <input
              id="ret-age"
              v-model.number="policyForm.retirement_age_years"
              type="number"
              min="50"
              max="75"
              class="rm-input"
            >
            <span class="rm-hint">Expected retirement date = date of birth + this many years.</span>
          </div>

          <div class="rm-field-group">
            <h3 class="rm-field-group__title">Training / long-lead window</h3>
            <p class="rm-field-group__text">
              Officers whose retirement date falls between <strong>{{ policyForm.training_window_months_low }}</strong> and <strong>{{ policyForm.training_window_months_high }}</strong> months from today (e.g. pre-retirement training scheduling).
            </p>
            <div class="rm-inline">
              <div class="rm-field">
                <label for="tw-lo">Lower bound (months from today)</label>
                <input id="tw-lo" v-model.number="policyForm.training_window_months_low" type="number" min="0" max="120" class="rm-input">
              </div>
              <div class="rm-field">
                <label for="tw-hi">Upper bound (months from today)</label>
                <input id="tw-hi" v-model.number="policyForm.training_window_months_high" type="number" min="1" max="120" class="rm-input">
              </div>
            </div>
          </div>

          <div class="rm-field-group">
            <h3 class="rm-field-group__title">Email / short-lead window</h3>
            <p class="rm-field-group__text">
              Officers due for formal retirement communication when retirement is between <strong>{{ policyForm.email_window_months_low || 0 }}</strong> and <strong>{{ policyForm.email_window_months_high }}</strong> months ahead (typically the final year).
            </p>
            <div class="rm-inline">
              <div class="rm-field">
                <label for="ew-lo">Lower bound (months)</label>
                <input id="ew-lo" v-model.number="policyForm.email_window_months_low" type="number" min="0" max="120" class="rm-input">
              </div>
              <div class="rm-field">
                <label for="ew-hi">Upper bound (months)</label>
                <input id="ew-hi" v-model.number="policyForm.email_window_months_high" type="number" min="1" max="120" class="rm-input">
              </div>
            </div>
          </div>

          <div class="rm-field">
            <label for="notif-mo">List “notification due” (months before retirement)</label>
            <input
              id="notif-mo"
              v-model.number="policyForm.notification_months_before_retirement"
              type="number"
              min="1"
              max="60"
              class="rm-input"
            >
            <span class="rm-hint">Used for list columns only. Automated individual notices use calendar days below.</span>
          </div>

          <div class="rm-field-group">
            <h3 class="rm-field-group__title">Automated staff notice &amp; terminal leave (calendar days)</h3>
            <p class="rm-field-group__text">
              The daily job notifies each officer, auto-initiates terminal leave, and opens multi-department ESS clearance
              based on these periods.
            </p>
            <div class="rm-inline">
              <div class="rm-field">
                <label for="staff-days">Notify staff (days before retirement)</label>
                <input id="staff-days" v-model.number="policyForm.staff_notice_days_before" type="number" min="1" max="730" class="rm-input">
              </div>
              <div class="rm-field">
                <label for="tl-before">Auto terminal leave (days before retirement)</label>
                <input id="tl-before" v-model.number="policyForm.terminal_leave_days_before" type="number" min="1" max="365" class="rm-input">
              </div>
              <div class="rm-field">
                <label for="tl-days">Terminal leave days granted</label>
                <input id="tl-days" v-model.number="policyForm.terminal_leave_days_granted" type="number" min="1" max="365" class="rm-input">
              </div>
              <div class="rm-field">
                <label for="ess-days">ESS clearance window (calendar days)</label>
                <input id="ess-days" v-model.number="policyForm.ess_action_window_days" type="number" min="1" max="365" class="rm-input">
                <span class="rm-hint">e.g. 30 — employee submits to departments in self-service for that period.</span>
              </div>
            </div>
            <div class="rm-inline" style="margin-top:0.75rem">
              <label class="rm-check"><input v-model="policyForm.auto_notify_enabled" type="checkbox" true-value="1" false-value="0"> Auto-notify staff</label>
              <label class="rm-check"><input v-model="policyForm.auto_terminal_leave_enabled" type="checkbox" true-value="1" false-value="0"> Auto-initiate terminal leave</label>
              <label class="rm-check"><input v-model="policyForm.auto_clearance_enabled" type="checkbox" true-value="1" false-value="0"> Auto-open ESS clearance</label>
            </div>
          </div>
        </div>

        <div class="rm-actions">
          <button type="button" class="rm-btn rm-btn--primary" :disabled="policySaving" @click="savePolicy">
            {{ policySaving ? "Saving…" : "Save policy" }}
          </button>
          <button type="button" class="rm-btn rm-btn--secondary" :disabled="runningAutomation" @click="runAutomationNow">
            {{ runningAutomation ? "Running…" : "Run automation now" }}
          </button>
        </div>
      </section>

      <!-- Notifications -->
      <section v-show="activeTab === 'notifications'" class="rm-panel">
        <header class="rm-panel__head rm-panel__head--row">
          <div>
            <h2 class="rm-panel__title">Retirement notifications</h2>
            <p class="rm-panel__desc">
              Lists use retirement age <strong>{{ policy.retirement_age_years }}</strong> years and the windows configured under Policy.
            </p>
          </div>
        </header>

        <div class="rm-stats">
          <div class="rm-stat">
            <span class="rm-stat__label">{{ trackerLabelTraining }}</span>
            <span class="rm-stat__value">{{ dailyTracker.twoYears }}</span>
          </div>
          <div class="rm-stat">
            <span class="rm-stat__label">{{ trackerLabelEmail }}</span>
            <span class="rm-stat__value">{{ dailyTracker.oneYear }}</span>
          </div>
          <div class="rm-stat rm-stat--accent">
            <span class="rm-stat__label">Retirement date today</span>
            <span class="rm-stat__value">{{ dailyTracker.reachedToday }}</span>
          </div>
          <div class="rm-stat rm-stat--muted">
            <span class="rm-stat__label">Tracker refreshed</span>
            <span class="rm-stat__value rm-stat__value--sm">{{ dailyTracker.lastUpdated || "—" }}</span>
          </div>
        </div>

        <div class="rm-toolbar">
          <div class="rm-toolbar__group">
            <label class="rm-sr-only" for="rm-mode">View</label>
            <select id="rm-mode" v-model="notificationMode" class="rm-select" @change="loadNotifications">
              <option
                v-for="opt in notificationModeOptions"
                :key="opt.value"
                :value="opt.value"
              >
                {{ opt.label }}
              </option>
            </select>
            <select v-model="notificationDepartmentId" class="rm-select" @change="loadNotifications">
              <option value="">All departments</option>
              <option v-for="d in departments" :key="d.id || d.departmentID" :value="d.id || d.departmentID">
                {{ d.departmentName || d.name || d.department }}
              </option>
            </select>
          </div>
          <button type="button" class="rm-btn rm-btn--secondary" @click="exportNotificationsReport">
            Export CSV
          </button>
        </div>

        <div class="rm-split">
          <div class="rm-mini-card">
            <h3 class="rm-mini-card__title">Gender</h3>
            <table class="rm-table rm-table--compact">
              <tbody>
                <tr v-for="g in genderBreakdown" :key="g.label">
                  <td>{{ g.label }}</td>
                  <td class="rm-num">{{ g.count }}</td>
                </tr>
                <tr v-if="!genderBreakdown.length">
                  <td colspan="2" class="rm-empty">No rows in this view</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="rm-mini-card">
            <h3 class="rm-mini-card__title">Department</h3>
            <table class="rm-table rm-table--compact">
              <tbody>
                <tr v-for="d in departmentBreakdown" :key="d.label">
                  <td>{{ d.label }}</td>
                  <td class="rm-num">{{ d.count }}</td>
                </tr>
                <tr v-if="!departmentBreakdown.length">
                  <td colspan="2" class="rm-empty">No rows in this view</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="rm-table-wrap">
          <table class="rm-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Department</th>
                <th>Gender</th>
                <th>Date of birth</th>
                <th>Age</th>
                <th>Retirement date</th>
                <th>Notification due</th>
                <th>Months left</th>
                <th>Sent</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="n in retirementNotifications" :key="n.employee_id || `${n.firstName}-${n.lastName}-${n.retirement_date}`">
                <td class="rm-emp">{{ n.firstName }} {{ n.lastName }}</td>
                <td>{{ n.department_name }}</td>
                <td>{{ n.gender || "—" }}</td>
                <td>{{ n.dateOfBirth || "—" }}</td>
                <td class="rm-num">{{ n.age_years }}</td>
                <td>{{ n.retirement_date }}</td>
                <td>{{ n.notification_due_date }}</td>
                <td class="rm-num">{{ n.months_to_retirement }}</td>
                <td>{{ n.notification_sent_at ? "Yes" : "No" }}</td>
                <td>
                  <button
                    v-if="notificationMode === 'training_two_years'"
                    type="button"
                    class="rm-btn rm-btn--sm rm-btn--primary"
                    @click="scheduleTraining(n)"
                  >
                    Schedule training
                  </button>
                  <button
                    v-else-if="notificationMode === 'reached_today'"
                    type="button"
                    class="rm-btn rm-btn--sm rm-btn--ghost"
                    :disabled="sendingEmailId === n.employee_id"
                    @click="sendRetirementEmail(n)"
                  >
                    {{ sendingEmailId === n.employee_id ? "Sending…" : "Send email" }}
                  </button>
                  <button
                    v-else
                    type="button"
                    class="rm-btn rm-btn--sm rm-btn--primary"
                    :disabled="sendingEmailId === n.employee_id"
                    @click="sendRetirementEmail(n)"
                  >
                    {{ sendingEmailId === n.employee_id ? "Sending…" : "Send email" }}
                  </button>
                </td>
              </tr>
              <tr v-if="!retirementNotifications.length">
                <td colspan="10" class="rm-empty">No employees match this view.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- Succession -->
      <section v-show="activeTab === 'succession'" class="rm-panel">
        <header class="rm-panel__head rm-panel__head--row">
          <h2 class="rm-panel__title">Succession planning</h2>
          <button type="button" class="rm-btn rm-btn--primary" @click="openSuccessionModal()">Add plan</button>
        </header>
        <div class="rm-table-wrap">
          <table class="rm-table">
            <thead>
              <tr>
                <th>Position</th>
                <th>Department</th>
                <th>Incumbent</th>
                <th>Successor</th>
                <th>Planned date</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in successionPlans" :key="p.id">
                <td>{{ p.position_title || "—" }}</td>
                <td>{{ p.department_id }}</td>
                <td>{{ employeeLabel(p.incumbent_employee_id) }}</td>
                <td>{{ employeeLabel(p.successor_employee_id) }}</td>
                <td>{{ p.planned_date || "—" }}</td>
                <td><span class="rm-pill">{{ p.status }}</span></td>
                <td><button type="button" class="rm-btn rm-btn--sm rm-btn--ghost" @click="openSuccessionModal(p)">Edit</button></td>
              </tr>
              <tr v-if="!successionPlans.length">
                <td colspan="7" class="rm-empty">No succession plans recorded.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- Training -->
      <section v-show="activeTab === 'training'" class="rm-panel">
        <header class="rm-panel__head rm-panel__head--row">
          <h2 class="rm-panel__title">Pre-retirement training</h2>
          <div class="rm-toolbar__group">
            <select v-model="trainingEmployeeId" class="rm-select" @change="loadTraining">
              <option value="">All employees</option>
              <option v-for="e in employees" :key="e.id" :value="e.id">{{ e.firstName }} {{ e.lastName }}</option>
            </select>
            <button type="button" class="rm-btn rm-btn--primary" @click="openTrainingModal()">Add record</button>
            <button type="button" class="rm-btn rm-btn--secondary" @click="exportTrainingReport">Export CSV</button>
          </div>
        </header>
        <div class="rm-table-wrap">
          <table class="rm-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Department</th>
                <th>ID</th>
                <th>Topic</th>
                <th>Date</th>
                <th>Provider</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="t in preRetirementTraining" :key="t.id">
                <td>{{ `${t.firstName || ""} ${t.lastName || ""}`.trim() || "—" }}</td>
                <td>{{ t.departmentName || "—" }}</td>
                <td>{{ t.employee_id }}</td>
                <td>{{ t.training_topic }}</td>
                <td>{{ t.training_date }}</td>
                <td>{{ t.provider || "—" }}</td>
                <td><span class="rm-pill">{{ t.status }}</span></td>
                <td>
                  <button type="button" class="rm-btn rm-btn--sm rm-btn--ghost" @click="openTrainingModal(t)">Edit</button>
                </td>
              </tr>
              <tr v-if="!preRetirementTraining.length">
                <td colspan="8" class="rm-empty">No training records.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <!-- Clearance -->
      <section v-show="activeTab === 'clearance'" class="rm-panel">
        <header class="rm-panel__head">
          <h2 class="rm-panel__title">Exit clearance</h2>
          <p class="rm-panel__desc">
            Multi-department retirement clearances opened automatically within the ESS action window.
            Officers submit from self-service; HR/departments clear each step here.
          </p>
        </header>
        <div class="rm-table-wrap">
          <table class="rm-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Retirement date</th>
                <th>Window</th>
                <th>Type</th>
                <th>Status</th>
                <th>Progress</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="c in exitClearances" :key="c.id">
                <td>{{ c.firstName }} {{ c.lastName }}</td>
                <td>{{ c.retirement_date || "—" }}</td>
                <td class="rm-small">{{ c.window_start || "—" }} → {{ c.window_end || "—" }}</td>
                <td>{{ c.clearance_type }}</td>
                <td><span class="rm-pill">{{ c.status }}</span></td>
                <td class="rm-small">{{ clearanceProgress(c) }}</td>
                <td><button type="button" class="rm-btn rm-btn--sm rm-btn--ghost" @click="openClearanceModal(c)">Manage steps</button></td>
              </tr>
              <tr v-if="!exitClearances.length">
                <td colspan="7" class="rm-empty">No exit clearance records. Run automation when staff enter the ESS window.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div v-if="clearanceModal" class="rm-modal-backdrop" @click.self="clearanceModal = null">
          <div class="rm-modal">
            <header class="rm-modal__head">
              <h3>Clearance — {{ clearanceModal.firstName }} {{ clearanceModal.lastName }}</h3>
              <button type="button" class="rm-btn rm-btn--ghost" @click="clearanceModal = null">Close</button>
            </header>
            <table class="rm-table">
              <thead>
                <tr><th>Department</th><th>Status</th><th>Submitted</th><th>Action</th></tr>
              </thead>
              <tbody>
                <tr v-for="s in clearanceSteps" :key="s.code">
                  <td>
                    <strong>{{ s.label }}</strong>
                    <div class="rm-hint" v-if="s.employee_notes">{{ s.employee_notes }}</div>
                  </td>
                  <td>{{ s.status }}</td>
                  <td class="rm-small">{{ s.employee_submitted_at || "—" }}</td>
                  <td>
                    <button
                      v-if="String(s.status).toLowerCase() !== 'cleared'"
                      type="button"
                      class="rm-btn rm-btn--sm rm-btn--primary"
                      :disabled="clearanceActing"
                      @click="actionClearanceStep(s, 'clear')"
                    >Clear</button>
                    <button
                      v-if="String(s.status).toLowerCase() === 'submitted'"
                      type="button"
                      class="rm-btn rm-btn--sm rm-btn--ghost"
                      :disabled="clearanceActing"
                      @click="actionClearanceStep(s, 'reject')"
                    >Reject</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <!-- Training modal -->
      <div v-if="showTrainingModal" class="rm-modal-backdrop" @click.self="showTrainingModal = false">
        <div class="rm-modal">
          <header class="rm-modal__head">
            <h3>{{ trainingForm.id ? "Edit training record" : "Add training record" }}</h3>
            <button type="button" class="rm-btn rm-btn--ghost" @click="showTrainingModal = false">Close</button>
          </header>
          <div class="rm-form">
            <div class="rm-field">
              <label>Employee <span class="rm-req">*</span></label>
              <select v-model="trainingForm.employee_id" class="rm-input" required>
                <option value="">Select employee</option>
                <option v-for="e in employees" :key="e.id" :value="String(e.id)">
                  {{ e.firstName }} {{ e.lastName }} — {{ e.departmentName || "" }}
                </option>
              </select>
            </div>
            <div class="rm-field">
              <label>Topic <span class="rm-req">*</span></label>
              <input v-model="trainingForm.training_topic" type="text" class="rm-input" placeholder="e.g. Pre-retirement preparedness">
            </div>
            <div class="rm-inline">
              <div class="rm-field">
                <label>Training date</label>
                <input v-model="trainingForm.training_date" type="date" class="rm-input">
              </div>
              <div class="rm-field">
                <label>Provider</label>
                <input v-model="trainingForm.provider" type="text" class="rm-input" placeholder="HR Department">
              </div>
              <div class="rm-field">
                <label>Status</label>
                <select v-model="trainingForm.status" class="rm-input">
                  <option>Scheduled</option>
                  <option>Completed</option>
                  <option>Cancelled</option>
                  <option>No-show</option>
                </select>
              </div>
            </div>
            <div class="rm-field">
              <label>Notes</label>
              <textarea v-model="trainingForm.notes" class="rm-input" rows="2"></textarea>
            </div>
            <div class="rm-actions">
              <button type="button" class="rm-btn rm-btn--ghost" @click="showTrainingModal = false">Cancel</button>
              <button type="button" class="rm-btn rm-btn--primary" :disabled="trainingSaving" @click="saveTraining">
                {{ trainingSaving ? "Saving…" : "Save record" }}
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Succession modal -->
      <div v-if="showSuccessionModal" class="rm-modal-backdrop" @click.self="showSuccessionModal = false">
        <div class="rm-modal">
          <header class="rm-modal__head">
            <h3>{{ successionForm.id ? "Edit succession plan" : "Add succession plan" }}</h3>
            <button type="button" class="rm-btn rm-btn--ghost" @click="showSuccessionModal = false">Close</button>
          </header>
          <div class="rm-form">
            <div class="rm-field">
              <label>Position title <span class="rm-req">*</span></label>
              <input v-model="successionForm.position_title" type="text" class="rm-input" placeholder="e.g. Chief Officer Health">
            </div>
            <div class="rm-inline">
              <div class="rm-field">
                <label>Department ID</label>
                <select v-model="successionForm.department_id" class="rm-input">
                  <option value="">Select</option>
                  <option v-for="d in departments" :key="d.id || d.departmentID" :value="String(d.id || d.departmentID)">
                    {{ d.departmentName || d.name }}
                  </option>
                </select>
              </div>
              <div class="rm-field">
                <label>Planned date</label>
                <input v-model="successionForm.planned_date" type="date" class="rm-input">
              </div>
              <div class="rm-field">
                <label>Status</label>
                <select v-model="successionForm.status" class="rm-input">
                  <option>Draft</option>
                  <option>Active</option>
                  <option>Completed</option>
                  <option>Cancelled</option>
                </select>
              </div>
            </div>
            <div class="rm-inline">
              <div class="rm-field">
                <label>Incumbent (retiring officer)</label>
                <select v-model="successionForm.incumbent_employee_id" class="rm-input">
                  <option value="">Select</option>
                  <option v-for="e in employees" :key="'i'+e.id" :value="String(e.id)">
                    {{ e.firstName }} {{ e.lastName }}
                  </option>
                </select>
              </div>
              <div class="rm-field">
                <label>Successor</label>
                <select v-model="successionForm.successor_employee_id" class="rm-input">
                  <option value="">Select</option>
                  <option v-for="e in employees" :key="'s'+e.id" :value="String(e.id)">
                    {{ e.firstName }} {{ e.lastName }}
                  </option>
                </select>
              </div>
            </div>
            <div class="rm-field">
              <label>Notes</label>
              <textarea v-model="successionForm.successor_notes" class="rm-input" rows="2"></textarea>
            </div>
            <div class="rm-actions">
              <button type="button" class="rm-btn rm-btn--ghost" @click="showSuccessionModal = false">Cancel</button>
              <button type="button" class="rm-btn rm-btn--primary" :disabled="successionSaving" @click="saveSuccession">
                {{ successionSaving ? "Saving…" : "Save plan" }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { execute, authUrl, toast } from "@/api";

const defaultPolicy = () => ({
  retirement_age_years: 60,
  training_window_months_high: 24,
  training_window_months_low: 12,
  email_window_months_high: 12,
  email_window_months_low: 0,
  notification_months_before_retirement: 12,
  staff_notice_days_before: 90,
  terminal_leave_days_before: 30,
  terminal_leave_days_granted: 30,
  ess_action_window_days: 30,
  auto_notify_enabled: 1,
  auto_terminal_leave_enabled: 1,
  auto_clearance_enabled: 1
});

export default {
  name: "RetirementManagement",
  components: { HrPageLayout },
  data() {
    return {
      activeTab: "notifications",
      tabs: [
        { id: "notifications", label: "Notifications", icon: "bx-bell" },
        { id: "policy", label: "Policy & settings", icon: "bx-cog" },
        { id: "succession", label: "Succession", icon: "bx-git-branch" },
        { id: "training", label: "Training", icon: "bx-book-open" },
        { id: "clearance", label: "Exit clearance", icon: "bx-check-shield" }
      ],
      policy: defaultPolicy(),
      policyForm: defaultPolicy(),
      policySaving: false,
      retirementNotifications: [],
      successionPlans: [],
      preRetirementTraining: [],
      exitClearances: [],
      clearanceModal: null,
      clearanceActing: false,
      showTrainingModal: false,
      trainingSaving: false,
      trainingForm: {
        id: "",
        employee_id: "",
        training_topic: "",
        training_date: "",
        provider: "HR Department",
        status: "Scheduled",
        notes: ""
      },
      showSuccessionModal: false,
      successionSaving: false,
      successionForm: {
        id: "",
        position_title: "",
        department_id: "",
        planned_date: "",
        status: "Draft",
        incumbent_employee_id: "",
        successor_employee_id: "",
        successor_notes: ""
      },
      employees: [],
      trainingEmployeeId: "",
      departments: [],
      notificationDepartmentId: "",
      notificationMode: "training_two_years",
      sendingEmailId: null,
      runningAutomation: false,
      dailyTracker: {
        twoYears: 0,
        oneYear: 0,
        reachedToday: 0,
        lastUpdated: ""
      }
    };
  },
  computed: {
    trackerLabelTraining() {
      const p = this.policy;
      return `Training window (${p.training_window_months_low}–${p.training_window_months_high} mo)`;
    },
    trackerLabelEmail() {
      const p = this.policy;
      const lo = p.email_window_months_low || 0;
      return `Email window (${lo}–${p.email_window_months_high} mo)`;
    },
    notificationModeOptions() {
      const p = this.policy;
      const age = p.retirement_age_years;
      const tLo = p.training_window_months_low;
      const tHi = p.training_window_months_high;
      const eLo = p.email_window_months_low || 0;
      const eHi = p.email_window_months_high;
      return [
        {
          value: "training_two_years",
          label: `Training list (${tLo}–${tHi} months to retirement)`
        },
        {
          value: "email_one_year",
          label: `Email list (${eLo}–${eHi} months to retirement)`
        },
        {
          value: "reached_today",
          label: `Retirement date today (age ${age})`
        }
      ];
    },
    genderBreakdown() {
      const map = {};
      this.retirementNotifications.forEach((row) => {
        const k = (row.gender || "Unknown").toString().trim() || "Unknown";
        map[k] = (map[k] || 0) + 1;
      });
      return Object.keys(map).sort().map((k) => ({ label: k, count: map[k] }));
    },
    departmentBreakdown() {
      const map = {};
      this.retirementNotifications.forEach((row) => {
        const k = (row.department_name || "Unassigned").toString().trim() || "Unassigned";
        map[k] = (map[k] || 0) + 1;
      });
      return Object.keys(map).sort().map((k) => ({ label: k, count: map[k] }));
    },
    clearanceSteps() {
      if (!this.clearanceModal) return [];
      try {
        const steps = typeof this.clearanceModal.steps === "string"
          ? JSON.parse(this.clearanceModal.steps)
          : this.clearanceModal.steps;
        return Array.isArray(steps) ? steps : [];
      } catch (e) {
        return [];
      }
    }
  },
  created() {
    this.loadEmployees();
    this.loadDepartments();
    this.loadPolicy().then(() => {
      this.policyForm = { ...this.policy };
      this.load();
    });
  },
  methods: {
    applyPolicy(data) {
      if (!data || typeof data !== "object") return;
      this.policy = { ...defaultPolicy(), ...this.policy, ...data };
    },
    loadPolicy() {
      const fd = new FormData();
      fd.append("function", "getRetirementPolicySettings");
      return execute(fd, authUrl)
        .then((res) => {
          const s = res?.data?.data?.settings;
          if (s) {
            this.applyPolicy(s);
            this.policyForm = { ...this.policy };
          }
        })
        .catch(() => {});
    },
    savePolicy() {
      this.policySaving = true;
      const fd = new FormData();
      fd.append("function", "saveRetirementPolicySettings");
      fd.append("retirement_age_years", String(this.policyForm.retirement_age_years));
      fd.append("training_window_months_high", String(this.policyForm.training_window_months_high));
      fd.append("training_window_months_low", String(this.policyForm.training_window_months_low));
      fd.append("email_window_months_high", String(this.policyForm.email_window_months_high));
      fd.append("email_window_months_low", String(this.policyForm.email_window_months_low));
      fd.append("notification_months_before_retirement", String(this.policyForm.notification_months_before_retirement));
      fd.append("staff_notice_days_before", String(this.policyForm.staff_notice_days_before));
      fd.append("terminal_leave_days_before", String(this.policyForm.terminal_leave_days_before));
      fd.append("terminal_leave_days_granted", String(this.policyForm.terminal_leave_days_granted));
      fd.append("ess_action_window_days", String(this.policyForm.ess_action_window_days));
      fd.append("auto_notify_enabled", String(this.policyForm.auto_notify_enabled ? 1 : 0));
      fd.append("auto_terminal_leave_enabled", String(this.policyForm.auto_terminal_leave_enabled ? 1 : 0));
      fd.append("auto_clearance_enabled", String(this.policyForm.auto_clearance_enabled ? 1 : 0));
      execute(fd, authUrl)
        .then((res) => {
          if (res?.data?.success) {
            const s = res?.data?.data?.settings;
            if (s) this.applyPolicy(s);
            this.policyForm = { ...this.policy };
            toast.success("Retirement policy saved.");
            this.loadNotifications();
            this.loadDailyTracker();
          } else {
            toast.error(res?.data?.message || "Could not save policy.");
          }
        })
        .catch(() => toast.error("Could not save policy."))
        .finally(() => { this.policySaving = false; });
    },
    runAutomationNow() {
      this.runningAutomation = true;
      const fd = new FormData();
      fd.append("function", "runRetirementAutomationNow");
      execute(fd, authUrl)
        .then((res) => {
          if (res?.data?.success) {
            const d = res.data.data || {};
            toast.success(
              `Done: notices ${d.notices_sent || 0}, terminal leave ${d.terminal_leave_initiated || 0}, clearance ${d.clearance_opened || 0}.`
            );
            this.loadNotifications();
            this.loadClearances();
          } else {
            toast.error(res?.data?.message || "Automation failed.");
          }
        })
        .catch(() => toast.error("Automation failed."))
        .finally(() => { this.runningAutomation = false; });
    },
    load() {
      if (this.activeTab === "notifications") this.loadNotifications();
      else if (this.activeTab === "succession") this.loadSuccession();
      else if (this.activeTab === "training") this.loadTraining();
      else if (this.activeTab === "clearance") this.loadClearances();
    },
    loadEmployees() {
      const fd = new FormData();
      fd.append("function", "getEmployees");
      fd.append("page", "1");
      fd.append("limit", "500");
      execute(fd, authUrl).then((res) => {
        if (res.data && res.data.success && res.data.data && res.data.data.employees)
          this.employees = res.data.data.employees;
      }).catch(() => {});
    },
    loadDepartments() {
      const fd = new FormData();
      fd.append("function", "getDepartments");
      execute(fd, authUrl).then((res) => {
        if (res.data && res.data.success && res.data.data && res.data.data.departments)
          this.departments = res.data.data.departments;
      }).catch(() => {});
    },
    loadNotifications() {
      const fd = new FormData();
      fd.append("function", "getRetirementNotifications");
      fd.append("mode", this.notificationMode);
      if (this.notificationDepartmentId) fd.append("departmentId", this.notificationDepartmentId);
      execute(fd, authUrl).then((res) => {
        const pol = res?.data?.data?.policy;
        if (pol) this.applyPolicy(pol);
        const rows = (res.data && res.data.success && res.data.data && res.data.data.notifications) ? res.data.data.notifications : [];
        if (rows.length) {
          this.retirementNotifications = rows;
          this.loadDailyTracker();
          return;
        }
        this.loadNotificationsFromRegisterFallback().finally(() => {
          this.loadDailyTracker();
        });
      }).catch(() => { this.retirementNotifications = []; });
    },
    parseDobFlexible(v) {
      const raw = String(v || "").trim();
      if (!raw) return null;
      if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return new Date(`${raw}T00:00:00`);
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(raw)) {
        const [a, b, y] = raw.split("/").map(Number);
        const d1 = new Date(y, b - 1, a);
        if (d1.getFullYear() === y && d1.getMonth() === b - 1 && d1.getDate() === a) return d1;
        const d2 = new Date(y, a - 1, b);
        if (d2.getFullYear() === y && d2.getMonth() === a - 1 && d2.getDate() === b) return d2;
      }
      if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) {
        const [d, m, y] = raw.split("-").map(Number);
        const dt = new Date(y, m - 1, d);
        if (dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d) return dt;
      }
      const dt = new Date(raw);
      return Number.isNaN(dt.getTime()) ? null : dt;
    },
    formatDate(dt) {
      if (!dt) return "";
      const y = dt.getFullYear();
      const m = String(dt.getMonth() + 1).padStart(2, "0");
      const d = String(dt.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    },
    addYears(dt, years) {
      const x = new Date(dt.getTime());
      x.setFullYear(x.getFullYear() + years);
      return x;
    },
    addMonths(dt, months) {
      const x = new Date(dt.getTime());
      x.setMonth(x.getMonth() + months);
      return x;
    },
    stripTime(d) {
      return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    },
    yearsBetween(dob, today) {
      let y = today.getFullYear() - dob.getFullYear();
      const m = today.getMonth() - dob.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) y -= 1;
      return y;
    },
    loadNotificationsFromRegisterFallback() {
      const fd = new FormData();
      fd.append("function", "getEmployees");
      fd.append("page", "1");
      fd.append("limit", "10000");
      return execute(fd, authUrl).then((res) => {
        const employees = res?.data?.data?.employees || [];
        const today = this.stripTime(new Date());
        const p = this.policy;
        const ageY = p.retirement_age_years;
        const tLo = p.training_window_months_low;
        const tHi = p.training_window_months_high;
        const eLo = p.email_window_months_low || 0;
        const eHi = p.email_window_months_high;
        const nMo = p.notification_months_before_retirement;
        const out = [];
        employees.forEach((e) => {
          if (this.notificationDepartmentId && String(e.departmentID || e.departmentId || "") !== String(this.notificationDepartmentId)) return;
          const dobRaw = e.dateOfBirth || e.dob || e.date_of_birth || "";
          const dob = this.parseDobFlexible(dobRaw);
          if (!dob) return;
          const retirement = this.stripTime(this.addYears(dob, ageY));
          const monthsLeft = (retirement.getFullYear() - today.getFullYear()) * 12 + (retirement.getMonth() - today.getMonth());
          const age = this.yearsBetween(dob, today);
          let include = false;
          const trainStart = this.stripTime(this.addMonths(today, tLo));
          const trainEnd = this.stripTime(this.addMonths(today, tHi));
          const emailStart = this.stripTime(eLo <= 0 ? today : this.addMonths(today, eLo));
          const emailEnd = this.stripTime(this.addMonths(today, eHi));
          if (this.notificationMode === "training_two_years") {
            include = retirement > trainStart && retirement <= trainEnd;
          } else if (this.notificationMode === "email_one_year") {
            include = retirement > emailStart && retirement <= emailEnd;
          } else {
            include = this.formatDate(retirement) === this.formatDate(today);
          }
          if (!include) return;
          out.push({
            employee_id: e.id,
            firstName: e.firstName || "",
            lastName: e.lastName || "",
            department_name: e.departmentName || "",
            gender: e.gender || "Unknown",
            dateOfBirth: typeof dobRaw === "string" ? dobRaw : this.formatDate(dob),
            age_years: age,
            retirement_date: this.formatDate(retirement),
            notification_due_date: this.formatDate(this.addMonths(retirement, -nMo)),
            notification_sent_at: null,
            months_to_retirement: monthsLeft
          });
        });
        this.retirementNotifications = out;
      }).catch(() => { this.retirementNotifications = []; });
    },
    loadDailyTracker() {
      const makeReq = (mode) => {
        const fd = new FormData();
        fd.append("function", "getRetirementNotifications");
        fd.append("mode", mode);
        if (this.notificationDepartmentId) fd.append("departmentId", this.notificationDepartmentId);
        return execute(fd, authUrl)
          .then((res) => ((res?.data?.data?.notifications || []).length))
          .catch(() => 0);
      };
      Promise.all([
        makeReq("training_two_years"),
        makeReq("email_one_year"),
        makeReq("reached_today")
      ]).then(([twoYears, oneYear, reachedToday]) => {
        const now = new Date();
        const stamp = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")} ${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
        this.dailyTracker = { twoYears, oneYear, reachedToday, lastUpdated: stamp };
      });
    },
    exportNotificationsReport() {
      const headers = [
        "Employee",
        "Department",
        "Gender",
        "Date Of Birth",
        "Age",
        "Retirement Date",
        "Notification Due Date",
        "Months To Retirement"
      ];
      const rows = this.retirementNotifications.map((n) => ([
        `${n.firstName || ""} ${n.lastName || ""}`.trim(),
        n.department_name || "",
        n.gender || "Unknown",
        n.dateOfBirth || "",
        n.age_years ?? "",
        n.retirement_date || "",
        n.notification_due_date || "",
        n.months_to_retirement ?? ""
      ]));
      const csv = [headers, ...rows]
        .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
        .join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `retirement_${this.notificationMode}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    sendRetirementEmail(row) {
      if (!row || !row.employee_id) return;
      this.sendingEmailId = row.employee_id;
      const fd = new FormData();
      fd.append("function", "sendRetirementNotificationEmail");
      fd.append("employeeId", row.employee_id);
      execute(fd, authUrl)
        .then((res) => {
          if (res?.data?.success) toast.success("Email sent.");
          else toast.error(res?.data?.message || "Failed to send email.");
        })
        .catch(() => toast.error("Failed to send email."))
        .finally(() => { this.sendingEmailId = null; });
    },
    scheduleTraining(row) {
      if (!row?.employee_id) return;
      const fd = new FormData();
      fd.append("function", "savePreRetirementTraining");
      fd.append("employee_id", String(row.employee_id));
      fd.append("training_topic", "Pre-retirement preparedness");
      fd.append("training_date", new Date().toISOString().slice(0, 10));
      fd.append("provider", "HR Department");
      fd.append("status", "Scheduled");
      const lo = this.policy.training_window_months_low;
      const hi = this.policy.training_window_months_high;
      fd.append("notes", `Auto-scheduled from training window (${lo}–${hi} months to retirement).`);
      execute(fd, authUrl)
        .then((res) => {
          if (res?.data?.success) {
            toast.success("Training scheduled.");
            this.trainingEmployeeId = String(row.employee_id);
            this.activeTab = "training";
            this.loadTraining();
          } else {
            toast.error(res?.data?.message || "Failed to schedule training.");
          }
        })
        .catch(() => toast.error("Failed to schedule training."));
    },
    loadSuccession() {
      const fd = new FormData();
      fd.append("function", "getSuccessionPlans");
      execute(fd, authUrl).then((res) => {
        this.successionPlans = (res.data && res.data.success && res.data.data && res.data.data.plans) ? res.data.data.plans : [];
      }).catch(() => { this.successionPlans = []; });
    },
    loadTraining() {
      const fd = new FormData();
      fd.append("function", "getPreRetirementTraining");
      if (this.trainingEmployeeId) fd.append("employeeId", this.trainingEmployeeId);
      execute(fd, authUrl).then((res) => {
        this.preRetirementTraining = (res.data && res.data.success && res.data.data && res.data.data.trainings) ? res.data.data.trainings : [];
      }).catch(() => { this.preRetirementTraining = []; });
    },
    exportTrainingReport() {
      const headers = ["Employee", "Department", "Employee ID", "Topic", "Date", "Provider", "Status"];
      const rows = this.preRetirementTraining.map((t) => ([
        `${t.firstName || ""} ${t.lastName || ""}`.trim(),
        t.departmentName || "",
        t.employee_id || "",
        t.training_topic || "",
        t.training_date || "",
        t.provider || "",
        t.status || ""
      ]));
      const csv = [headers, ...rows]
        .map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
        .join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pre_retirement_training_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    loadClearances() {
      const fd = new FormData();
      fd.append("function", "getExitClearanceList");
      execute(fd, authUrl).then((res) => {
        this.exitClearances = (res.data && res.data.success && res.data.data && res.data.data.clearances) ? res.data.data.clearances : [];
      }).catch(() => { this.exitClearances = []; });
    },
    clearanceProgress(c) {
      try {
        const steps = typeof c.steps === "string" ? JSON.parse(c.steps) : c.steps;
        if (!Array.isArray(steps) || !steps.length) return "—";
        const cleared = steps.filter((s) => String(s.status || "").toLowerCase() === "cleared").length;
        return `${cleared}/${steps.length} cleared`;
      } catch (e) {
        return "—";
      }
    },
    openClearanceModal(c) {
      this.clearanceModal = c ? { ...c } : null;
    },
    actionClearanceStep(step, action) {
      if (!this.clearanceModal?.id || !step?.code) return;
      this.clearanceActing = true;
      const fd = new FormData();
      fd.append("function", "actionRetirementClearanceStep");
      fd.append("clearance_id", String(this.clearanceModal.id));
      fd.append("step_code", step.code);
      fd.append("action", action);
      execute(fd, authUrl)
        .then((res) => {
          if (res?.data?.success) {
            toast.success(res.data.message || "Updated");
            const steps = res?.data?.data?.steps;
            if (Array.isArray(steps)) {
              this.clearanceModal = {
                ...this.clearanceModal,
                steps: JSON.stringify(steps),
                status: res.data.data.status || this.clearanceModal.status
              };
            }
            this.loadClearances();
          } else {
            toast.error(res?.data?.message || "Update failed");
          }
        })
        .catch(() => toast.error("Update failed"))
        .finally(() => { this.clearanceActing = false; });
    },
    openSuccessionModal(row) {
      if (row && row.id) {
        this.successionForm = {
          id: String(row.id),
          position_title: row.position_title || "",
          department_id: row.department_id != null ? String(row.department_id) : "",
          planned_date: row.planned_date ? String(row.planned_date).slice(0, 10) : "",
          status: row.status || "Draft",
          incumbent_employee_id: row.incumbent_employee_id != null ? String(row.incumbent_employee_id) : "",
          successor_employee_id: row.successor_employee_id != null ? String(row.successor_employee_id) : "",
          successor_notes: row.successor_notes || ""
        };
      } else {
        this.successionForm = {
          id: "",
          position_title: "",
          department_id: "",
          planned_date: "",
          status: "Draft",
          incumbent_employee_id: "",
          successor_employee_id: "",
          successor_notes: ""
        };
      }
      this.showSuccessionModal = true;
    },
    saveSuccession() {
      if (!String(this.successionForm.position_title || "").trim()) {
        toast.error("Position title is required.");
        return;
      }
      this.successionSaving = true;
      const fd = new FormData();
      fd.append("function", "saveSuccessionPlan");
      if (this.successionForm.id) fd.append("id", this.successionForm.id);
      fd.append("position_title", this.successionForm.position_title);
      fd.append("department_id", this.successionForm.department_id || "");
      if (this.successionForm.planned_date) fd.append("planned_date", this.successionForm.planned_date);
      fd.append("status", this.successionForm.status || "Draft");
      if (this.successionForm.incumbent_employee_id) {
        fd.append("incumbent_employee_id", this.successionForm.incumbent_employee_id);
      }
      if (this.successionForm.successor_employee_id) {
        fd.append("successor_employee_id", this.successionForm.successor_employee_id);
      }
      fd.append("successor_notes", this.successionForm.successor_notes || "");
      execute(fd, authUrl)
        .then((res) => {
          if (res?.data?.success) {
            toast.success(res.data.message || "Plan saved.");
            this.showSuccessionModal = false;
            this.loadSuccession();
          } else {
            toast.error(res?.data?.message || "Could not save plan.");
          }
        })
        .catch(() => toast.error("Could not save plan."))
        .finally(() => { this.successionSaving = false; });
    },
    openTrainingModal(row) {
      if (row && (row.id || row.employee_id)) {
        this.trainingForm = {
          id: row.id ? String(row.id) : "",
          employee_id: row.employee_id != null ? String(row.employee_id) : "",
          training_topic: row.training_topic || "Pre-retirement preparedness",
          training_date: row.training_date ? String(row.training_date).slice(0, 10) : new Date().toISOString().slice(0, 10),
          provider: row.provider || "HR Department",
          status: row.status || "Scheduled",
          notes: row.notes || ""
        };
      } else {
        this.trainingForm = {
          id: "",
          employee_id: this.trainingEmployeeId ? String(this.trainingEmployeeId) : "",
          training_topic: "Pre-retirement preparedness",
          training_date: new Date().toISOString().slice(0, 10),
          provider: "HR Department",
          status: "Scheduled",
          notes: ""
        };
      }
      this.showTrainingModal = true;
    },
    saveTraining() {
      if (!this.trainingForm.employee_id) {
        toast.error("Select an employee.");
        return;
      }
      if (!String(this.trainingForm.training_topic || "").trim()) {
        toast.error("Topic is required.");
        return;
      }
      this.trainingSaving = true;
      const fd = new FormData();
      fd.append("function", "savePreRetirementTraining");
      if (this.trainingForm.id) fd.append("id", this.trainingForm.id);
      fd.append("employee_id", this.trainingForm.employee_id);
      fd.append("training_topic", this.trainingForm.training_topic);
      if (this.trainingForm.training_date) fd.append("training_date", this.trainingForm.training_date);
      fd.append("provider", this.trainingForm.provider || "");
      fd.append("status", this.trainingForm.status || "Scheduled");
      fd.append("notes", this.trainingForm.notes || "");
      execute(fd, authUrl)
        .then((res) => {
          if (res?.data?.success) {
            toast.success(res.data.message || "Training saved.");
            this.showTrainingModal = false;
            this.loadTraining();
          } else {
            toast.error(res?.data?.message || "Could not save training.");
          }
        })
        .catch(() => toast.error("Could not save training."))
        .finally(() => { this.trainingSaving = false; });
    },
    employeeLabel(id) {
      if (id == null || id === "") return "—";
      const e = (this.employees || []).find((x) => String(x.id) === String(id));
      if (e) return `${e.firstName || ""} ${e.lastName || ""}`.trim() || String(id);
      return String(id);
    }
  }
};
</script>

<style scoped>
.rm-module {
  max-width: 1400px;
  margin: 0 auto;
}

.rm-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 20px;
  padding: 4px;
  background: rgba(255, 255, 255, 0.85);
  border-radius: 14px;
  border: 1px solid rgba(226, 232, 240, 0.95);
  box-shadow: 0 2px 12px rgba(13, 79, 115, 0.06);
}

.rm-tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: #475569;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, box-shadow 0.2s;
}

.rm-tab:hover {
  background: #f1f5f9;
  color: #0d4f73;
}

.rm-tab--active {
  background: linear-gradient(135deg, #0d4f73 0%, #1478b0 100%);
  color: #fff;
  box-shadow: 0 4px 14px rgba(13, 79, 115, 0.25);
}

.rm-tab__icon {
  font-size: 17px;
  opacity: 0.9;
}
.rm-tab--active .rm-tab__icon {
  opacity: 1;
}

.rm-panel {
  background: #fff;
  border-radius: 16px;
  border: 1px solid rgba(226, 232, 240, 0.9);
  box-shadow: 0 4px 24px rgba(13, 79, 115, 0.07);
  padding: 24px 28px 28px;
  margin-bottom: 20px;
}

.rm-panel__head {
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e8f4fc;
}

.rm-panel__head--row {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.rm-panel__title {
  margin: 0 0 6px;
  font-size: 1.15rem;
  font-weight: 700;
  color: #0d4f73;
  letter-spacing: -0.02em;
}

.rm-panel__desc {
  margin: 0;
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
  max-width: 720px;
}

.rm-policy-grid {
  display: flex;
  flex-direction: column;
  gap: 22px;
}

.rm-field-group {
  padding: 16px;
  border-radius: 12px;
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
}

.rm-field-group__title {
  margin: 0 0 8px;
  font-size: 14px;
  font-weight: 700;
  color: #0d4f73;
}

.rm-field-group__text {
  margin: 0 0 14px;
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
}

.rm-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.rm-field--highlight {
  padding: 16px;
  border-radius: 12px;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
}

.rm-field label {
  font-size: 12px;
  font-weight: 600;
  color: #334155;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.rm-input {
  max-width: 280px;
  padding: 10px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 10px;
  font-size: 14px;
  color: #0f172a;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.rm-input:focus {
  outline: none;
  border-color: #2195d3;
  box-shadow: 0 0 0 3px rgba(33, 149, 211, 0.2);
}

.rm-hint {
  font-size: 12px;
  color: #64748b;
}

.rm-inline {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.rm-actions {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #e8f4fc;
}

.rm-stats {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
}

.rm-stat {
  padding: 14px 16px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.rm-stat--accent {
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  border-color: #a7f3d0;
}

.rm-stat--muted {
  background: #f1f5f9;
}

.rm-stat__label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #64748b;
  margin-bottom: 6px;
  line-height: 1.3;
}

.rm-stat__value {
  font-size: 1.5rem;
  font-weight: 800;
  color: #0d4f73;
  letter-spacing: -0.03em;
}

.rm-stat__value--sm {
  font-size: 13px;
  font-weight: 600;
}

.rm-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
}

.rm-toolbar__group {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.rm-select {
  min-width: 220px;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #cbd5e1;
  font-size: 13px;
  background: #fff;
  color: #0f172a;
}

.rm-split {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}

.rm-mini-card {
  padding: 14px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #fafafa;
}

.rm-mini-card__title {
  margin: 0 0 10px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
}

.rm-table-wrap {
  overflow-x: auto;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.rm-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.rm-table thead {
  background: linear-gradient(180deg, #f8fafc 0%, #f1f5f9 100%);
}

.rm-table th {
  text-align: left;
  padding: 12px 14px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #475569;
  border-bottom: 1px solid #e2e8f0;
}

.rm-table td {
  padding: 11px 14px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
}

.rm-table tbody tr:hover {
  background: #f8fafc;
}

.rm-table--compact td {
  padding: 8px 10px;
}

.rm-num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.rm-emp {
  font-weight: 600;
  color: #0d4f73;
}

.rm-empty {
  text-align: center;
  color: #94a3b8;
  padding: 24px !important;
}

.rm-pill {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  background: #e8f4fc;
  color: #0d4f73;
}

.rm-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 18px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: transform 0.15s, box-shadow 0.2s;
}

.rm-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.rm-btn--primary {
  background: linear-gradient(135deg, #2195d3 0%, #0d4f73 100%);
  color: #fff;
  box-shadow: 0 2px 10px rgba(33, 149, 211, 0.35);
}

.rm-btn--primary:hover:not(:disabled) {
  box-shadow: 0 4px 16px rgba(33, 149, 211, 0.45);
}

.rm-btn--secondary {
  background: #fff;
  color: #0d4f73;
  border: 1px solid #cbd5e1;
}

.rm-btn--ghost {
  background: transparent;
  color: #1478b0;
  border: 1px solid #bae6fd;
}

.rm-btn--sm {
  padding: 6px 12px;
  font-size: 12px;
}

.rm-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  border: 0;
}

.rm-check {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 13px;
  color: #334155;
  margin-right: 1rem;
}

.rm-small {
  font-size: 12px;
  color: #64748b;
}

.rm-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}

.rm-modal {
  background: #fff;
  border-radius: 14px;
  max-width: 720px;
  width: 100%;
  max-height: 85vh;
  overflow: auto;
  padding: 1.25rem 1.5rem;
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.18);
}

.rm-modal__head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.rm-modal__head h3 {
  margin: 0;
  font-size: 1.1rem;
}

.rm-form {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.rm-req {
  color: #b91c1c;
}

.rm-form .rm-field label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 4px;
}

.rm-form .rm-input,
.rm-form select.rm-input,
.rm-form textarea.rm-input {
  width: 100%;
  padding: 10px 12px;
  border-radius: 10px;
  border: 1px solid #cbd5e1;
  font-size: 13px;
  font-family: inherit;
  background: #fff;
}

@media (max-width: 640px) {
  .rm-select {
    min-width: 100%;
  }
}
</style>
